import React, { useState } from 'react';

import { useGraphQLMutation } from '@betterment/graphql';
import { Box, Button, Checkbox, FormikField, H1, Text } from '@betterment/samba-react';
import { Form, Formik } from 'formik';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

import { Header } from './Header';

import AdvisoryAgreements from '../../Agreements/AdvisoryAgreements';
import TermsAndConditions from '../../Agreements/TermsAndConditions';
import { AdvisedSoloFourOhOneKClientInviteStatus } from '../../graphql/schema';
import { CompleteSolo401kClientEnvelopeFlowDocument } from '../graphql/mutation.completeSolo401kClientEnvelopeFlow.generated';

interface AccountAgreementsProps {
  logoUrl?: string;
  firmName: string;
  clientAgreementUrl?: string | null;
  clientAgreementToken?: string | null;
}

const getFormSchema = (hasClientAgreement: boolean) => {
  const baseSchema = {
    termsAndConditions: Yup.boolean().oneOf([true], 'You must accept this agreement to continue'),
  };

  if (hasClientAgreement) {
    return Yup.object().shape({
      ...baseSchema,
      advisoryAgreement: Yup.boolean().oneOf([true], 'You must accept this agreement to continue'),
    });
  }

  return Yup.object().shape(baseSchema);
};

export const AccountAgreements: React.FC<AccountAgreementsProps> = ({
  logoUrl,
  firmName,
  clientAgreementUrl,
  clientAgreementToken,
}) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [completeSolo401kFlow] = useGraphQLMutation(CompleteSolo401kClientEnvelopeFlowDocument);

  const handleSubmit = async (values: { termsAndConditions: boolean; advisoryAgreement?: boolean }) => {
    setErrorMessage(null);
    const invitationToken = params.get('invitation_token');

    if (!invitationToken) {
      setErrorMessage('Missing invitation token');
      return;
    }

    try {
      const { data } = await completeSolo401kFlow({
        variables: {
          input: {
            inviteId: invitationToken,
            termsAndConditionsAccepted: values.termsAndConditions,
            advisoryAgreementAccepted: values.advisoryAgreement || null,
          },
        },
      });

      const errors = data?.completeSolo401kClientEnvelopeFlow?.displayableErrors;

      if (errors && errors.length > 0) {
        setErrorMessage(errors[0].message);
        return;
      }

      const result = data?.completeSolo401kClientEnvelopeFlow?.result;
      if (result?.status === AdvisedSoloFourOhOneKClientInviteStatus.Complete) {
        await navigate(`/accept_solo_401k/success?invitation_token=${invitationToken}`);
      }
    } catch (error) {
      setErrorMessage('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'column' }}>
      <Header logoUrl={logoUrl} progress={99} title="Account agreements" />

      <Box
        css={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignSelf: 'center',
          padding: 'space.400',
          paddingTop: 'space.800',
          width: '672px',
        }}
      >
        <H1
          variant="pageTitle"
          css={{
            marginBottom: 'space.150',
            color: 'text.DEFAULT',
            paddingBottom: 'space.200',
          }}
        >
          Review these agreements to open your account
        </H1>

        <Formik
          initialValues={{
            termsAndConditions: false,
            ...(clientAgreementUrl && { advisoryAgreement: false }),
          }}
          onSubmit={handleSubmit}
          validationSchema={getFormSchema(!!clientAgreementUrl)}
        >
          {({ isSubmitting }) => (
            <Form>
              {errorMessage && (
                <Box css={{ marginBottom: 'space.200' }}>
                  <Text color="text.critical">{errorMessage}</Text>
                </Box>
              )}
              <Box css={{ marginBottom: 'space.200' }}>
                <FormikField name="termsAndConditions">
                  <FormikField.Control>
                    <Checkbox>
                      <Checkbox.Control />
                      <Checkbox.Label>
                        By checking this box, you acknowledge that you have read and agree to the terms below, and
                        acknowledge the receipt of the documents contained therein.
                      </Checkbox.Label>
                    </Checkbox>
                  </FormikField.Control>
                  <FormikField.ErrorMessage />
                </FormikField>
              </Box>

              <TermsAndConditions includeSolo401kCopy={true} />

              {clientAgreementUrl && (
                <>
                  <Box css={{ marginTop: 'space.200' }}>
                    <FormikField name="advisoryAgreement">
                      <FormikField.Control>
                        <Checkbox>
                          <Checkbox.Control />
                          <Checkbox.Label>
                            By checking this box, you acknowledge and agree that you have read and agreed to the terms
                            below.
                          </Checkbox.Label>
                        </Checkbox>
                      </FormikField.Control>
                      <FormikField.ErrorMessage />
                    </FormikField>
                  </Box>

                  <AdvisoryAgreements
                    firmName={firmName}
                    agreements={{
                      clientAgreement: {
                        url: clientAgreementUrl,
                        token: clientAgreementToken!,
                      },
                    }}
                  />
                </>
              )}

              <Button type="submit" variant="solidPrimary" disabled={isSubmitting} css={{ marginTop: 'space.200' }}>
                Confirm Account
              </Button>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};
